import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { answersMatch } from "./unscrambleUtils";

const ANSWER_MAX_LEN = 30;
const INPUT_TONES = 5;

function normalizeGuess(s) {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
}

/** True when typed text can still become the answer (prefix match). */
function isAnswerPrefix(guess, answer) {
  const g = normalizeGuess(guess);
  const a = normalizeGuess(answer);
  if (!g) return true;
  return a.startsWith(g);
}

const UnscrambleRow = forwardRef(function UnscrambleRow({
  index,
  scrambled,
  answer,
  done,
  onSolved,
  textStyle,
}, ref) {
  const [value, setValue] = useState("");
  const [rowState, setRowState] = useState("idle");
  const inputRef = useRef(null);
  const tone = (index % INPUT_TONES) + 1;

  useImperativeHandle(ref, () => ({
    focusFirst() {
      inputRef.current?.focus();
    },
  }));

  useEffect(() => {
    if (done) {
      setRowState("correct");
      setValue(String(answer ?? "").trim());
    }
  }, [done, answer]);

  const tryMatch = useCallback(
    (raw) => {
      const guess = String(raw ?? "").trim();
      if (!guess) {
        setRowState("idle");
        return;
      }
      if (answersMatch(guess, answer)) {
        setRowState("correct");
        onSolved(index);
        return;
      }
      // Red while typing only when the text can no longer match the answer
      setRowState(isAnswerPrefix(guess, answer) ? "idle" : "wrong");
    },
    [answer, index, onSolved]
  );

  const handleChange = (e) => {
    if (done || rowState === "correct") return;
    const next = String(e.target.value ?? "").slice(0, ANSWER_MAX_LEN);
    setValue(next);
    tryMatch(next);
  };

  const handleBlur = () => {
    if (done || rowState === "correct") return;
    const guess = String(value).trim();
    if (guess && !answersMatch(guess, answer)) {
      setRowState("wrong");
    }
  };

  return (
    <div
      className={`unscramble-stage__row${
        rowState === "correct" || done ? " unscramble-stage__row--done" : ""
      }${rowState === "wrong" ? " unscramble-stage__row--wrong" : ""}`}
    >
      <div className="unscramble-stage__scrambled" style={textStyle}>
        <span className="unscramble-stage__num-badge" aria-hidden>
          {index + 1}
        </span>
        <span className="unscramble-stage__scrambled-text">{scrambled}</span>
      </div>

      <textarea
        ref={inputRef}
        rows={2}
        wrap="soft"
        className={`unscramble-stage__answer-input unscramble-stage__answer-input--tone-${tone}${
          rowState === "correct" ? " unscramble-stage__answer-input--correct" : ""
        }${rowState === "wrong" ? " unscramble-stage__answer-input--wrong" : ""}`}
        value={value}
        maxLength={ANSWER_MAX_LEN}
        placeholder="Type your answer"
        disabled={done || rowState === "correct"}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.preventDefault();
        }}
        aria-label={`Answer for word ${index + 1}`}
      />
    </div>
  );
});

export default UnscrambleRow;
